import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  FiMenu, FiX, FiShoppingBag, FiHome, FiUser, 
  FiLogOut, FiInfo, FiMail, FiSearch,
  FiXCircle, FiFileText, FiShoppingCart, FiTrash2, FiPlus, FiMinus
} from 'react-icons/fi';
import { FaTelegramPlane } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { API_ENDPOINTS } from '../utils/constants';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [cartCount, setCartCount] = useState(0);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const { isAuth, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const cartPanelRef = useRef(null);

  // Telegram username for contact
  const TELEGRAM_USERNAME = '+85510612636';
  const PHONE_NUMBER = '+85510612636';
  const WEBSITE_URL = 'https://grandfathersmile.com';

  // Load cart from localStorage on mount
  useEffect(() => {
    loadCartFromStorage();
  }, []);

  // Load cart from localStorage
  const loadCartFromStorage = () => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const cart = JSON.parse(savedCart);
        setCartItems(cart);
        const totalCount = cart.reduce((sum, item) => sum + item.quantity, 0);
        setCartCount(totalCount);
      } catch (error) {
        console.error('Error loading cart:', error);
      }
    }
  };

  // Save cart to localStorage
  const saveCartToStorage = (cart) => {
    localStorage.setItem('cart', JSON.stringify(cart));
    const totalCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    setCartCount(totalCount);
    setCartItems(cart);
  };

  // Update cart count when cart changes
  useEffect(() => {
    const handleCartUpdate = () => {
      loadCartFromStorage();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, []);

  // Close cart when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cartPanelRef.current && !cartPanelRef.current.contains(event.target) && 
          !event.target.closest('.cart-trigger')) {
        setIsCartOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when cart is open
  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isCartOpen]);

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch search suggestions from API
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.trim().length > 0) {
        try {
          const response = await api.get(API_ENDPOINTS.PRODUCTS, {
            params: { 
              search: searchQuery,
              limit: 8
            }
          });
          const products = response.data.data || response.data;
          const suggestions = products.map(product => product.title);
          setSearchSuggestions(suggestions.slice(0, 8));
          setShowSuggestions(true);
          setSelectedSuggestionIndex(-1);
        } catch (err) {
          console.error('Error fetching suggestions:', err);
          const localSuggestions = [
            'អង្ករស្រូវថ្មី',
            'ត្រីប្រាសាទ',
            'បន្លែសរីរាង្គ',
            'ផ្លែឈើស្រស់',
            'ទឹកត្រីឆ្ងាញ់',
            'ម្រេចកំពត',
            'ស្វាយចន្ទី',
            'ដូង'
          ].filter(product => 
            product.toLowerCase().includes(searchQuery.toLowerCase())
          );
          setSearchSuggestions(localSuggestions.slice(0, 8));
          setShowSuggestions(true);
        }
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchSuggestions();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    toast.success('ចាកចេញដោយជោគជ័យ');
    navigate('/');
  };

  const handleSearch = (e) => {
  e.preventDefault();
  if (searchQuery.trim()) {
    const query = searchQuery.trim();
    const encodedQuery = encodeURIComponent(query);
    
    // Check if search query contains "បោះដុំ"
    if (query.includes('បោះដុំ')) {
      // Redirect to wholesale page
      navigate(`/products-wholesale?search=${encodedQuery}`);
    } else {
      // Redirect to retail page
      navigate(`/products-retail?search=${encodedQuery}`);
    }
    
    setSearchQuery('');
    setIsSearchOpen(false);
    setIsOpen(false);
    setShowSuggestions(false);
  }
};

  const handleSuggestionClick = (suggestion) => {
  const query = suggestion;
  const encodedQuery = encodeURIComponent(query);
  
  // Check if suggestion contains "បោះដុំ"
  if (query.includes('បោះដុំ')) {
    // Redirect to wholesale page
    navigate(`/products-wholesale?search=${encodedQuery}`);
  } else {
    // Redirect to retail page
    navigate(`/products-retail?search=${encodedQuery}`);
  }
  
  setIsSearchOpen(false);
  setIsOpen(false);
  setShowSuggestions(false);
};

  const handleKeyDown = (e) => {
    if (searchSuggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev < searchSuggestions.length - 1 ? prev + 1 : prev
      );
    }
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedSuggestionIndex >= 0 && searchSuggestions[selectedSuggestionIndex]) {
        handleSuggestionClick(searchSuggestions[selectedSuggestionIndex]);
      } else {
        handleSearch(e);
      }
    }
    else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  const handleTelegramContact = () => {
    const message = encodeURIComponent(
      `សួស្តី! ខ្ញុំចាប់អារម្មណ៍ចង់ទិញផលិតផលពី GrandFatherSmile។\n\n` +
      `សូមផ្តល់ព័ត៌មានផលិតផល និងតម្លៃផង។\n\n` +
      `អរគុណ!`
    );
    window.open(`https://t.me/${TELEGRAM_USERNAME}?text=${message}`, '_blank');
    toast.success('បើក Telegram ដើម្បីទំនាក់ទំនង');
  };

  // Cart functions
  const handleRemoveFromCart = (productId) => {
    const updatedCart = cartItems.filter(item => item.id !== productId);
    saveCartToStorage(updatedCart);
    toast.success('ដកផលិតផលចេញពីកន្ត្រក');
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      handleRemoveFromCart(productId);
      return;
    }
    
    const updatedCart = cartItems.map(item =>
      item.id === productId ? { ...item, quantity: newQuantity } : item
    );
    saveCartToStorage(updatedCart);
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const getCartTotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('កន្ត្រកទំនេរ សូមបន្ថែមផលិតផល');
      return;
    }
    
    const cartDetails = cartItems.map(item => 
      `${item.title} x ${item.quantity} = $${(item.price * item.quantity).toFixed(2)}`
    ).join('\n');
    
    const total = getCartTotal();
    const message = encodeURIComponent(
      `🛒 *ការបញ្ជាទិញថ្មី* 🛒\n\n` +
      `*ផលិតផល:*\n${cartDetails}\n\n` +
      `*សរុប:* $${total.toFixed(2)}\n\n` +
      `សូមផ្តល់ព័ត៌មានបន្ថែមអំពីការដឹកជញ្ជូន និងការទូទាត់ផង។`
    );
    
    window.open(`https://t.me/${TELEGRAM_USERNAME}?text=${message}`, '_blank');
    setIsCartOpen(false);
    toast.success('បញ្ជូនព័ត៌មានទៅកាន់ Telegram');
  };

  const clearCart = () => {
    if (window.confirm('តើអ្នកចង់សម្អាតកន្ត្រកទាំងមូលមែនទេ?')) {
      saveCartToStorage([]);
      toast.success('សម្អាតកន្ត្រកដោយជោគជ័យ');
      window.dispatchEvent(new Event('cartUpdated'));
    }
  };

  const navLinks = [
    { name: 'ទំព័រដើម', path: '/', icon: FiHome },
    { name: 'លក់រាយ', path: '/products-retail', icon: FiShoppingBag },
    { name: 'បោះដុំ', path: '/products-wholesale', icon: FiShoppingBag },
    { name: 'អត្ថបទ', path: '/repairs', icon: FiFileText },
    { name: 'អំពីយើង', path: '/about', icon: FiInfo },
    { name: 'ទំនាក់ទំនង', path: '/contact', icon: FiMail },
  ];

  const bottomNavItems = [
    { name: 'ទំព័រដើម', path: '/', icon: FiHome },
    { name: 'លក់រាយ', path: '/products-retail', icon: FiShoppingBag },
    { name: 'បោះដុំ', path: '/products-wholesale', icon: FiShoppingBag },
    { name: 'កន្ត្រក', path: '#', icon: FiShoppingCart, onClick: () => setIsCartOpen(true) },
    
  ];

  const isActivePath = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img 
                src="/logo11.png" 
                alt="GrandFatherSmile"
                className="h-14 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/logo3.png";
                }}
              />
              <div className="flex flex-col">
                <span className="text-xl font-bold text-gray-800">
                  GrandFatherSmile
                </span>
                <span className="text-xs text-gray-500 -mt-1">
                  សញ្ញាតាញញឹម
                </span>
              </div>
            </Link>

            {/* Desktop Navigation - Center */}
            <div className="hidden md:flex items-center space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    isActivePath(link.path)
                      ? 'text-red-600 bg-red-50'
                      : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-2">
              {/* Desktop Search Bar with Dropdown */}
              <div className="hidden lg:block relative" ref={searchRef}>
                <form onSubmit={handleSearch} className="flex items-center">
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
                    placeholder="ស្វែងរកផលិតផល..."
                    className="w-30 px-4 py-2 border border-gray-200 bg-gray-50 text-gray-700 text-sm focus:outline-none focus:border-red-600 focus:bg-white rounded-l"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery('');
                        setShowSuggestions(false);
                        inputRef.current?.focus();
                      }}
                      className="absolute right-10 text-gray-400 hover:text-red-600"
                    >
                      <FiXCircle size={16} />
                    </button>
                  )}
                  <button 
                    type="submit" 
                    className="px-4 py-2 bg-gray-100 border border-l-0 border-gray-200 text-gray-600 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors rounded-r"
                  >
                    <FiSearch size={18} />
                  </button>
                </form>

                {/* Search Suggestions Dropdown */}
                {showSuggestions && searchSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 shadow-lg z-50 max-h-80 overflow-y-auto rounded">
                    {searchSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        onMouseEnter={() => setSelectedSuggestionIndex(index)}
                        className={`w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors border-b border-gray-100 last:border-0 ${
                          selectedSuggestionIndex === index ? 'bg-red-50 text-red-600' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <FiSearch size={14} className="text-gray-400" />
                          <span>{suggestion}</span>
                        </div>
                      </button>
                    ))}
                    <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">
                      ចុច Enter ដើម្បីស្វែងរកទាំងអស់
                    </div>
                  </div>
                )}
              </div>

              {/* Cart Icon with Badge - Fixed on right side */}
              <div className="cart-trigger">
                <button
                  onClick={() => setIsCartOpen(true)}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 relative rounded"
                >
                  <FiShoppingCart size={22} />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                      {cartCount > 9 ? '9+' : cartCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Mobile Search Icon */}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="lg:hidden p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
              >
                <FiSearch size={20} />
              </button>

              {/* Buy Now Button - Opens Telegram */}
              <button
                onClick={handleTelegramContact}
                className="hidden lg:flex items-center gap-2 bg-red-600 text-white px-4 py-2 font-medium text-sm hover:bg-red-700 transition-colors rounded"
              >
                <FaTelegramPlane size={16} />
                <span>ទិញឥឡូវនេះ</span>
              </button>

              {/* User Menu - Desktop (Only show if logged in) */}
              {isAuth && (
                <div className="hidden md:flex items-center">
                  <Link 
                    to="/profile" 
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Profile"
                  >
                    <FiUser size={20} />
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Logout"
                  >
                    <FiLogOut size={20} />
                  </button>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="md:hidden p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                aria-label="Toggle menu"
              >
                {isOpen ? <FiX size={20} /> : <FiMenu size={20} />}
              </button>
            </div>
          </div>

          {/* Mobile Search Bar with Dropdown */}
          <div 
            className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
              isSearchOpen ? 'max-h-96 py-3' : 'max-h-0'
            }`}
          >
            <div className="relative">
              <form onSubmit={handleSearch} className="flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="ស្វែងរកផលិតផល..."
                  className="flex-1 px-4 py-2 border border-gray-200 bg-gray-50 text-gray-700 text-sm focus:outline-none focus:border-red-600 rounded-l"
                  autoFocus={isSearchOpen}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-12 text-gray-400 hover:text-red-600"
                  >
                    <FiXCircle size={16} />
                  </button>
                )}
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-gray-100 border border-l-0 border-gray-200 text-gray-600 hover:bg-red-600 hover:text-white rounded-r"
                >
                  <FiSearch size={18} />
                </button>
              </form>

              {/* Mobile Search Suggestions */}
              {isSearchOpen && showSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 shadow-lg z-50 max-h-60 overflow-y-auto rounded">
                  {searchSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className={`w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors border-b border-gray-100 last:border-0 ${
                        selectedSuggestionIndex === index ? 'bg-red-50 text-red-600' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <FiSearch size={14} className="text-gray-400" />
                        <span>{suggestion}</span>
                      </div>
                    </button>
                  ))}
                  <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">
                    ចុច Enter ដើម្បីស្វែងរកទាំងអស់
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu - Slides from right */}
        <div 
          className={`md:hidden fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
            isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          onClick={() => setIsOpen(false)}
        >
          <div 
            className={`absolute right-0 top-0 bottom-0 w-4/5 max-w-sm bg-white shadow-xl transition-transform duration-300 ease-in-out ${
              isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <span className="font-bold text-gray-800">ម៉ឺនុយ</span>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-red-50 hover:text-red-600 rounded"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="p-4">
              <div className="space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center gap-3 px-4 py-3 transition-colors rounded ${
                      isActivePath(link.path)
                        ? 'text-red-600 bg-red-50'
                        : 'text-gray-700 hover:bg-red-50 hover:text-red-600'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    <link.icon className="text-lg" />
                    <span className="font-medium">{link.name}</span>
                  </Link>
                ))}
                
                <button
                  onClick={() => {
                    setIsCartOpen(true);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors rounded"
                >
                  <FiShoppingCart className="text-lg" />
                  <span className="font-medium">កន្ត្រកទិញទំនិញ</span>
                  {cartCount > 0 && (
                    <span className="ml-auto bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </button>
              </div>

              <div className="h-px bg-gray-200 my-4" />

              <button
                onClick={() => {
                  handleTelegramContact();
                  setIsOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 bg-red-600 text-white px-4 py-3 font-medium hover:bg-red-700 mb-3 rounded"
              >
                <FaTelegramPlane size={18} />
                <span>ទិញឥឡូវនេះ</span>
              </button>

              {isAuth ? (
                <>
                  <div className="px-4 py-3 bg-gray-50 mb-2 rounded">
                    <div className="font-medium text-gray-800">
                      {user?.email?.split('@')[0]}
                    </div>
                    <div className="text-sm text-gray-600 mt-0.5">{user?.email}</div>
                  </div>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded"
                  >
                    <FiLogOut className="text-lg" />
                    <span className="font-medium">ចាកចេញ</span>
                  </button>
                </>
              ) : (
                <div className="text-center text-sm text-gray-500">
                  <p>ទំនាក់ទំនងតាម Telegram</p>
                  <p className="text-red-600 mt-1">@GrandFatherSmile</p>
                  <p className="text-gray-400 text-xs mt-2">ទូរស័ព្ទ: {PHONE_NUMBER}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Slide-in Cart Panel from Right */}
      <div 
        className={`fixed inset-0 z-50 transition-all duration-300 ease-in-out ${
          isCartOpen ? 'visible' : 'invisible'
        }`}
      >
        {/* Overlay */}
        <div 
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            isCartOpen ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={() => setIsCartOpen(false)}
        />
        
        {/* Cart Panel */}
        <div 
          ref={cartPanelRef}
          className={`absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl transition-transform duration-300 ease-in-out flex flex-col ${
            isCartOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Cart Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-2">
              <FiShoppingCart size={22} className="text-red-600" />
              <h2 className="text-lg font-bold text-gray-800">កន្ត្រកទិញទំនិញ</h2>
              
            </div>
            <button
              onClick={() => setIsCartOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FiX size={22} />
            </button>
          </div>

          {/* Cart Content */}
          <div className="flex-1 overflow-y-auto">
            {cartItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <FiShoppingCart size={48} className="text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">កន្ត្រកទំនេរ</h3>
                <p className="text-gray-500 text-sm mb-6">
                  សូមបន្ថែមផលិតផលទៅក្នុងកន្ត្រក
                </p>
                <button
                  onClick={() => {
                    setIsCartOpen(false);
                    navigate('/products');
                  }}
                  className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 transition-colors"
                >
                  ចាប់ផ្តើមទិញទំនិញ
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {cartItems.map((item) => (
                  <div key={item.id} className="p-4 flex gap-3 hover:bg-gray-50 transition-colors">
                    {/* Product Image */}
                    <div className="w-20 h-20 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                      {item.image ? (
                        <img 
                          src={item.image} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <FiShoppingBag size={32} />
                        </div>
                      )}
                    </div>
                    
                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-800 text-sm line-clamp-2">
                        {item.title}
                      </h4>
                      <p className="text-red-600 font-bold text-base mt-1">
                        ${item.price.toFixed(2)}
                      </p>
                      
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex items-center gap-2 bg-gray-100 rounded-lg">
                          <button
                            onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-red-600 hover:text-white rounded-l-lg transition-colors"
                          >
                            <FiMinus size={14} />
                          </button>
                          <span className="text-sm text-gray-700 min-w-[30px] text-center font-medium">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-red-600 hover:text-white rounded-r-lg transition-colors"
                          >
                            <FiPlus size={14} />
                          </button>
                        </div>
                        <button
                          onClick={() => handleRemoveFromCart(item.id)}
                          className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1"
                        >
                          <FiTrash2 size={14} />
                          <span>ដកចេញ</span>
                        </button>
                      </div>
                    </div>
                    
                    {/* Item Total */}
                    <div className="text-right">
                      <p className="text-sm text-gray-500">សរុប</p>
                      <p className="font-bold text-gray-800">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cart Footer */}
          {cartItems.length > 0 && (
            <div className="border-t border-gray-200 bg-white p-4 space-y-3">
              {/* Clear Cart Button */}
              <button
                onClick={clearCart}
                className="w-full flex items-center justify-center gap-2 border border-red-600 text-red-600 py-2 rounded hover:bg-red-50 transition-colors"
              >
                <FiTrash2 size={16} />
                <span>សម្អាតកន្ត្រកទាំងមូល</span>
              </button>

              {/* Total */}
              <div className="flex justify-between items-center pt-2">
                <span className="text-gray-600 font-medium">សរុបទាំងអស់:</span>
                <span className="text-2xl font-bold text-red-600">
                  ${getCartTotal().toFixed(2)}
                </span>
              </div>

              {/* Checkout Button */}
              <button
                onClick={handleCheckout}
                className="w-full bg-red-600 text-white py-3 font-medium hover:bg-red-700 transition-colors rounded-lg flex items-center justify-center gap-2"
              >
                <FaTelegramPlane size={18} />
                <span>បញ្ជាក់ការទិញតាម Telegram</span>
              </button>

              <p className="text-xs text-gray-500 text-center">
                * ទំនិញនឹងត្រូវបានបញ្ជូនតាម Telegram បន្ទាប់ពីបញ្ជាក់ការទិញ
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Navigation Bar */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 md:hidden shadow-lg">
          <div className="flex items-center justify-around h-14">
            {bottomNavItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = item.path !== '#' ? isActivePath(item.path) : false;
              
              if (item.onClick) {
                return (
                  <button
                    key={index}
                    onClick={item.onClick}
                    className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-gray-500 hover:text-red-600 relative"
                  >
                    <Icon size={20} />
                    <span className="text-xs mt-1 font-medium">{item.name}</span>
                    {item.name === 'កន្ត្រក' && cartCount > 0 && (
                      <span className="absolute -top-1 right-1/4 bg-red-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                        {cartCount}
                      </span>
                    )}
                  </button>
                );
              }
              
              return (
                <Link
                  key={index}
                  to={item.path}
                  className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                    isActive 
                      ? 'text-red-600' 
                      : 'text-gray-500 hover:text-red-600'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  <Icon size={20} />
                  <span className="text-xs mt-1 font-medium">{item.name}</span>
                  
                  {isActive && (
                    <span className="absolute bottom-0 w-8 h-0.5 bg-red-600"></span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;